import { motion } from "framer-motion";
import { Mail, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Logo from "../assets/fillopsLogo.png";
import { useUserAuth } from "../store/user";

const schema = z.object({
  email: z.string().email("Enter a valid email address"),
});

/**
 * /forgot-password — request a learner password-reset link.
 *
 *   POST /api/auth/forgot-password  { email }
 */
const ForgotPassword = () => {
  const navigate = useNavigate();
  const { userForgotPassword } = useUserAuth();
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({ resolver: zodResolver(schema) });

  // POST /api/auth/forgot-password — { email }
  const onSubmit = async (formData) => {
    setError("");
    setLoading(true);
    const result = await userForgotPassword({ email: formData.email });
    setLoading(false);
    if (!result.success) {
      setError(result.error || "Something went wrong, please try again.");
      return;
    }
    setSent(true);
  };

  return (
    <section className="min-h-screen flex items-center justify-center bg-white dark:bg-background px-3 py-6 transition-colors duration-300">
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative w-full max-w-sm bg-white dark:bg-background border border-gray-200 dark:border-gray-800 rounded-2xl shadow-lg p-6 transition-colors duration-300"
      >
        <div className="flex justify-center mb-3">
          <img src={Logo} alt="App Logo" className="h-14 w-14 rounded-full shadow-md object-cover" />
        </div>

        {sent ? (
          <div className="text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-green-500" />
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mt-3">Check your inbox</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">
              If an account exists for <span className="font-medium">{getValues("email")}</span>, we've sent a
              link to reset your password.
            </p>
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="mt-6 inline-flex items-center justify-center gap-2 w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 rounded-lg shadow-md transition duration-200"
            >
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </button>
          </div>
        ) : (
          <>
            <div className="text-center mb-5">
              <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Forgot password?</h1>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                Enter your email and we'll send you a reset link.
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-3.5 text-gray-400 h-5 w-5" />
                <input
                  type="email"
                  placeholder="Email Address"
                  autoComplete="email"
                  {...register("email")}
                  className={`w-full pl-10 pr-3 py-2.5 border rounded-lg text-gray-700 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 bg-transparent focus:outline-none focus:ring-2 focus:ring-indigo-400 transition ${
                    errors.email ? "border-red-400" : "border-gray-300 dark:border-gray-700"
                  }`}
                />
                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>}
              </div>

              {error && <p className="text-red-500 text-sm text-center">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                className={`w-full flex items-center justify-center gap-2 ${
                  loading ? "bg-indigo-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700"
                } text-white font-semibold py-2.5 rounded-lg shadow-md transition duration-200`}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                  </>
                ) : (
                  "Send reset link"
                )}
              </button>

              <button
                type="button"
                onClick={() => navigate("/login")}
                className="w-full inline-flex items-center justify-center gap-2 text-xs text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
              >
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </button>
            </form>
          </>
        )}
      </motion.div>
    </section>
  );
};

export default ForgotPassword;
